<template>
  <div v-if="tests.length > 0">
    <div class="bg-white px-6 py-4 my-4">
      <strong>Task Comments</strong>
      <p v-for="test in task.tests" :key="test.id">
        {{ test.farm_name }}: {{ test.task_comment || "None" }}
      </p>
    </div>
  </div>

  <!-- Add Sample Section -->
  <div class="bg-white px-6 py-4 my-4">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-medium text-gray-900">Samples</h3>
      <div class="flex space-x-2">
        <button 
          @click="toggleAddSampleForm('single')"
          :class="{
            'px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy': true,
            'text-white bg-navy hover:bg-navy-dark': !showAddSampleForm || sampleCreationMode === 'single',
            'text-gray-700 bg-gray-200 hover:bg-gray-300': showAddSampleForm && sampleCreationMode !== 'single'
          }"
        >
          {{ showAddSampleForm && sampleCreationMode === 'single' ? 'Cancel' : 'Add Sample' }}
        </button>
        <button 
          @click="toggleAddSampleForm('bulk')"
          :class="{
            'px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy': true,
            'text-white bg-green-600 hover:bg-green-700': !showAddSampleForm || sampleCreationMode === 'bulk',
            'text-gray-700 bg-gray-200 hover:bg-gray-300': showAddSampleForm && sampleCreationMode !== 'bulk'
          }"
        >
          {{ showAddSampleForm && sampleCreationMode === 'bulk' ? 'Cancel' : '⚡ Bulk Add' }}
        </button>
      </div>
    </div>

    <!-- Single Sample Creation Form -->
    <div v-if="showAddSampleForm && sampleCreationMode === 'single'" class="border border-gray-200 rounded-lg p-4 bg-gray-50 mb-4">
      <h4 class="text-md font-medium text-gray-900 mb-3">Add Single Sample</h4>
      <p class="text-gray-600">Single sample form - to be implemented</p>
    </div>
    
    <!-- Bulk Sample Creation Form -->
    <div v-if="showAddSampleForm && sampleCreationMode === 'bulk'" class="border border-green-200 rounded-lg p-4 bg-green-50 mb-4">
      <h4 class="text-md font-medium text-gray-900 mb-3">⚡ Bulk Sample Creation</h4>
      
      <!-- Step Progress Indicator -->
      <div class="mb-6">
        <nav aria-label="Progress">
          <ol class="flex items-center">
            <li class="relative pr-8 sm:pr-20">
              <div class="absolute inset-0 flex items-center" aria-hidden="true">
                <div class="h-0.5 w-full bg-green-200"></div>
              </div>
              <a href="#" class="relative flex h-8 w-8 items-center justify-center rounded-full bg-green-600 hover:bg-green-900">
                <span class="h-2.5 w-2.5 rounded-full bg-white" aria-hidden="true"></span>
                <span class="sr-only">Step 1</span>
              </a>
            </li>
            <li class="relative pr-8 sm:pr-20">
              <div class="absolute inset-0 flex items-center" aria-hidden="true">
                <div class="h-0.5 w-full bg-gray-200"></div>
              </div>
              <a href="#" :class="bulkStep >= 2 ? 'bg-green-600 hover:bg-green-900' : 'border-2 border-gray-300 bg-white hover:border-gray-400'" class="relative flex h-8 w-8 items-center justify-center rounded-full">
                <span :class="bulkStep >= 2 ? 'h-2.5 w-2.5 rounded-full bg-white' : 'h-2.5 w-2.5 rounded-full bg-transparent'" aria-hidden="true"></span>
                <span class="sr-only">Step 2</span>
              </a>
            </li>
            <li class="relative">
              <a href="#" :class="bulkStep >= 3 ? 'bg-green-600 hover:bg-green-900' : 'border-2 border-gray-300 bg-white hover:border-gray-400'" class="relative flex h-8 w-8 items-center justify-center rounded-full">
                <span :class="bulkStep >= 3 ? 'h-2.5 w-2.5 rounded-full bg-white' : 'h-2.5 w-2.5 rounded-full bg-transparent'" aria-hidden="true"></span>
                <span class="sr-only">Step 3</span>
              </a>
            </li>
          </ol>
        </nav>
        <div class="mt-2 flex justify-between text-sm text-gray-500">
          <span>Configure Samples</span>
          <span>Set Lab Tests</span>
          <span>Add Georeferences</span>
        </div>
      </div>

      <form @submit.prevent="createBulkSamples" class="space-y-6">
        <!-- Step 1: Configure Samples -->
        <div v-if="bulkStep === 1" class="space-y-4">
          <div class="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div class="flex">
              <div class="ml-3">
                <h3 class="text-sm font-medium text-blue-800">
                  📋 Step 1: Configure Your Samples
                </h3>
                <div class="mt-2 text-sm text-blue-700">
                  <p>Set up how many samples you want to create and how they should be numbered.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label for="bulk_count" class="block text-sm font-medium text-gray-700">Number of Samples *</label>
              <input 
                id="bulk_count"
                v-model.number="bulkSamples.count" 
                type="number" 
                min="1"
                max="50"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                placeholder="e.g. 5"
              />
              <p class="mt-1 text-xs text-gray-500">Maximum 50 samples at once</p>
            </div>
            
            <div>
              <label for="bulk_prefix" class="block text-sm font-medium text-gray-700">Sample ID Prefix</label>
              <input 
                id="bulk_prefix"
                v-model="bulkSamples.prefix" 
                type="text" 
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                placeholder="e.g. FIELD-A-"
              />
              <p class="mt-1 text-xs text-gray-500">Optional prefix for all sample IDs</p>
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label for="bulk_start" class="block text-sm font-medium text-gray-700">Starting Number</label>
              <input 
                id="bulk_start"
                v-model.number="bulkSamples.startNumber" 
                type="number" 
                min="1"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                placeholder="1"
              />
            </div>
            
            <div>
              <label for="bulk_suffix" class="block text-sm font-medium text-gray-700">Sample ID Suffix</label>
              <input 
                id="bulk_suffix"
                v-model="bulkSamples.suffix" 
                type="text" 
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                placeholder="e.g. -SOIL"
              />
              <p class="mt-1 text-xs text-gray-500">Optional suffix for all sample IDs</p>
            </div>
          </div>
          
          <!-- Preview Generated Sample IDs -->
          <div v-if="bulkSamples.count > 0" class="bg-gray-50 border border-gray-200 rounded-md p-3">
            <h4 class="text-sm font-medium text-gray-700 mb-2">Preview Sample IDs:</h4>
            <div class="flex flex-wrap gap-2">
              <span 
                v-for="(sampleId, index) in generateSampleIdPreviews().slice(0, 8)" 
                :key="index"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
              >
                {{ sampleId }}
              </span>
              <span v-if="bulkSamples.count > 8" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                ... and {{ bulkSamples.count - 8 }} more
              </span>
            </div>
          </div>
          
          <div class="flex justify-end">
            <button 
              type="button"
              @click="bulkStep = 2"
              :disabled="!bulkSamples.count || bulkSamples.count < 1"
              class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Next: Lab Tests →
            </button>
          </div>
        </div>

        <!-- Step 2: Lab Tests -->
        <div v-if="bulkStep === 2" class="space-y-4">
          <div class="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div class="flex">
              <div class="ml-3">
                <h3 class="text-sm font-medium text-blue-800">
                  🧪 Step 2: Select Lab Tests
                </h3>
                <div class="mt-2 text-sm text-blue-700">
                  <p>Choose which lab tests to apply to all samples. You can customize individual samples later.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700" for="bulk_lab_tests">Lab Tests for All Samples *</label>
            <Multiselect
              v-if="Object.keys(transformedLabTests).length > 0"
              v-model="bulkSamples.labTests"
              :options="transformedLabTests"
              :allow-empty="false"
              mode="tags"
              :close-on-select="false"
              :searchable="true"
              class="mt-1"
              placeholder="Select lab tests to apply to all samples"
            />
          </div>
          
          <div class="flex justify-between">
            <button 
              type="button"
              @click="bulkStep = 1"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              ← Back
            </button>
            <button 
              type="button"
              @click="bulkStep = 3"
              :disabled="!bulkSamples.labTests || bulkSamples.labTests.length === 0"
              class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Next: Georeferences →
            </button>
          </div>
        </div>

        <!-- Step 3: Georeferences -->
        <div v-if="bulkStep === 3" class="space-y-4">
          <div class="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div class="flex">
              <div class="ml-3">
                <h3 class="text-sm font-medium text-blue-800">
                  📍 Step 3: Set Georeferences
                </h3>
                <div class="mt-2 text-sm text-blue-700">
                  <p>Add georeference points for DAFM compliance. You can add one point per sample or assign the same points to all samples.</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Georeference Strategy Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Georeference Strategy</label>
            <div class="space-y-3">
              <label class="flex items-center">
                <input 
                  v-model="bulkSamples.georeferenceStrategy" 
                  type="radio" 
                  value="shared" 
                  class="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                />
                <span class="ml-2 text-sm text-gray-700">
                  <strong>Shared Location:</strong> Use the same georeference points for all samples (faster for samples from the same field)
                </span>
              </label>
              <label class="flex items-center">
                <input 
                  v-model="bulkSamples.georeferenceStrategy" 
                  type="radio" 
                  value="individual" 
                  class="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                />
                <span class="ml-2 text-sm text-gray-700">
                  <strong>Individual Locations:</strong> Set unique georeference points for each sample (more accurate but takes longer)
                </span>
              </label>
              <label class="flex items-center">
                <input 
                  v-model="bulkSamples.georeferenceStrategy" 
                  type="radio" 
                  value="skip" 
                  class="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                />
                <span class="ml-2 text-sm text-gray-700">
                  <strong>Skip for Now:</strong> Create samples without georeferences (you can add them later individually)
                </span>
              </label>
            </div>
          </div>
          
          <!-- Shared Georeference Selection -->
          <div v-if="bulkSamples.georeferenceStrategy === 'shared'" class="border border-gray-200 rounded-lg p-4 bg-white">
            <div class="flex items-center justify-between mb-3">
              <div class="text-sm text-gray-600">
                <p v-if="!isBulkGeoreferenceSelection">Click points on the map to set shared georeferences for all samples</p>
                <p v-else class="text-green-700 font-medium">📍 Click on the map to add shared points for all samples.</p>
              </div>
              
              <div class="flex items-center space-x-2">
                <button 
                  v-if="!isBulkGeoreferenceSelection"
                  type="button"
                  @click="startBulkGeoreferenceSelection"
                  class="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  🗺️ Select on Map
                </button>
                <button 
                  v-else
                  type="button"
                  @click="finishBulkGeoreferenceSelection"
                  class="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  ✓ Finish Selection
                </button>
                <button 
                  v-if="isBulkGeoreferenceSelection && bulkSamples.sharedGeoreferences.length > 0"
                  type="button"
                  @click="clearAllBulkGeoreferences"
                  class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  🗑️ Clear All
                </button>
              </div>
            </div>
            
            <!-- Selected Georeferences Display -->
            <div v-if="bulkSamples.sharedGeoreferences.length > 0" class="space-y-2">
              <div class="text-sm font-medium text-gray-700 mb-2">
                Shared Points for All Samples ({{ bulkSamples.sharedGeoreferences.length }}):
              </div>
              <div class="space-y-1 max-h-32 overflow-y-auto">
                <div 
                  v-for="(point, index) in bulkSamples.sharedGeoreferences" 
                  :key="index"
                  class="flex items-center justify-between py-2 px-3 bg-green-50 border border-green-200 rounded text-sm"
                >
                  <div>
                    <span class="font-medium text-green-800">Point {{ index + 1 }}:</span>
                    <span class="font-mono text-xs text-gray-700 ml-2">
                      {{ point.lat.toFixed(6) }}, {{ point.lng.toFixed(6) }}
                    </span>
                  </div>
                  <button 
                    type="button"
                    @click="removeBulkGeoreference(index)"
                    class="text-red-600 hover:text-red-800 text-xs px-2 py-1 rounded hover:bg-red-100"
                  >
                    ✕ Remove
                  </button>
                </div>
              </div>
            </div>
            
            <!-- No georeferences message -->
            <div v-else class="text-sm text-gray-500 italic">
              No georeferences selected. Click "Select on Map" to add shared points for all samples.
            </div>
          </div>
          
          <!-- Individual Strategy Notice -->
          <div v-if="bulkSamples.georeferenceStrategy === 'individual'" class="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div class="flex">
              <div class="ml-3">
                <h3 class="text-sm font-medium text-yellow-800">
                  ⚠️ Individual Georeference Mode
                </h3>
                <div class="mt-2 text-sm text-yellow-700">
                  <p>Samples will be created without georeferences. After creation, you'll need to add georeferences to each sample individually using the regular sample editing workflow.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="flex justify-between">
            <button 
              type="button"
              @click="bulkStep = 2"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              ← Back
            </button>
            <button 
              type="submit"
              :disabled="isCreatingBulk || (bulkSamples.georeferenceStrategy === 'shared' && bulkSamples.sharedGeoreferences.length === 0)"
              class="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <span v-if="isCreatingBulk">Creating {{ bulkSamples.count }} Samples...</span>
              <span v-else>✨ Create {{ bulkSamples.count }} Samples</span>
            </button>
          </div>
        </div>
      </form>
    </div>


        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label for="sample_id" class="block text-sm font-medium text-gray-700">Sample ID *</label>
            <input 
              id="sample_id"
              v-model="newSample.sample_id" 
              type="text" 
              required
              :class="{
                'mt-1 block w-full rounded-md shadow-sm focus:ring-navy sm:text-sm': true,
                'border-red-300 focus:border-red-500 focus:ring-red-500': isDuplicateSampleId(),
                'border-gray-300 focus:border-navy': !isDuplicateSampleId()
              }"
              placeholder="Enter sample ID"
            />
            <div v-if="isDuplicateSampleId()" class="mt-1 text-sm text-red-600">
              A sample with this ID already exists for this task
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700" for="lab_tests">Lab Test *</label>
            <Multiselect
              v-if="Object.keys(transformedLabTests).length > 0"
              v-model="newSample.lab_test"
              :options="transformedLabTests"
              :allow-empty="false"
              :max="1"
              mode="tags"
            />
            <div v-else class="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-500">
              No lab tests available for this task
            </div>
          </div>
          <div>
            <label for="comment" class="block text-sm font-medium text-gray-700">Comment</label>
            <input 
              id="comment"
              v-model="newSample.comment" 
              type="text" 
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-navy focus:ring-navy sm:text-sm"
              placeholder="Enter comment"
            />
          </div>
        </div>
        
        <!-- Georeferences Section -->
        <div class="col-span-full">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Georeferences * (DAFM Requirement - minimum 1 required)
          </label>
          
          <div class="border border-gray-200 rounded-lg p-4 bg-white">
            <div class="flex items-center justify-between mb-3">
              <div class="text-sm text-gray-600">
                <p v-if="!isSelectingGeoreferences">Click points on the map to set sample georeferences</p>
                <p v-else class="text-green-700 font-medium">📍 Click on the map to add points.</p>
                <p class="text-xs text-gray-500 mt-1">Use the map on the right to select coordinates for DAFM compliance</p>
              </div>
              <div class="flex items-center space-x-2">
                <button 
                  v-if="!isSelectingGeoreferences"
                  type="button"
                  @click="startGeoreferenceSelection"
                  class="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  🗺️ Select on Map
                </button>
                <button 
                  v-else
                  type="button"
                  @click="finishGeoreferenceSelection"
                  class="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  ✓ Finish Selection
                </button>
                <button 
                  v-if="isSelectingGeoreferences && newSample.georeferences.length > 0"
                  type="button"
                  @click="clearAllGeoreferences"
                  class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  🗑️ Clear All
                </button>
              </div>
            </div>
            
            <!-- Selected Georeferences Display -->
            <div v-if="newSample.georeferences.length > 0" class="space-y-2">
              <div class="text-sm font-medium text-gray-700 mb-2">
                Selected Points ({{ newSample.georeferences.length }}):
              </div>
              <div class="space-y-1 max-h-32 overflow-y-auto">
                <div 
                  v-for="(point, index) in newSample.georeferences" 
                  :key="index"
                  class="flex items-center justify-between py-2 px-3 bg-green-50 border border-green-200 rounded text-sm"
                >
                  <div>
                    <span class="font-medium text-green-800">Point {{ index + 1 }}:</span>
                    <span class="font-mono text-xs text-gray-700 ml-2">
                      {{ point.lat.toFixed(6) }}, {{ point.lng.toFixed(6) }}
                    </span>
                  </div>
                  <button 
                    type="button"
                    @click="removeGeoreference(index)"
                    class="text-red-600 hover:text-red-800 text-xs px-2 py-1 rounded hover:bg-red-100"
                  >
                    ✕ Remove
                  </button>
                </div>
              </div>
            </div>
            
            <!-- No georeferences message -->
            <div v-else class="text-sm text-gray-500 italic">
              No georeferences selected. Click "Select on Map" to add points.
            </div>
            
            <!-- Validation error -->
            <div v-if="showGeoreferenceError" class="mt-2 text-sm text-red-600">
              At least 1 georeference is required for DAFM compliance
            </div>
          </div>
        </div>

  </div>

  <div v-if="samples.length > 0" class="my-4 w-full">
    <div class="mt-2 pb-2 overflow-auto">
      <table
        class="table-auto min-w-full divide-y divide-gray-400 border-b border-gray-400 border-collapse rounded-lg"
      >
        <thead>
          <tr class="bg-gray-200 font-bold text-sm text-gray-900">
            <th scope="col" class="px-1 py-2.5 text-left">Sample ID</th>
            <th scope="col" class="px-1 py-2.5 text-left">Farm Name</th>
            <th scope="col" class="px-1 py-2.5 text-left">Lab Tests</th>
            <th scope="col" class="px-1 py-2.5 text-left">Sample Comment</th>
            <th scope="col" class="px-1 py-2.5 text-left">Sample Issues</th>
            <th scope="col" class="px-1 py-2.5 text-left">Matching Status</th>
            <th scope="col" class="px-1 py-2.5 text-left">Georeferences</th>
            <th scope="col" class="px-1 py-2.5 text-left">Lab Results</th>
            <th scope="col" class="px-1 py-2.5 text-left"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(sample, index) in samples"
            :key="index"
            class="h-14 odd:bg-white even:bg-slate-100 text-gray-900 text-sm"
            :class="index % 2 == 0 ? 'bg-white' : 'bg-gray-50'"
          >
            <td class="px-1 py-2.5">
              {{ sample.id }}
            </td>
            <td class="px-1 py-2.5">
              {{ sample.farm_name }}
            </td>
            <td class="px-1 py-2.5">
              {{ sample.lab_tests }}
            </td>
            <td class="px-1 py-2.5">
              {{ sample.comment }}
            </td>
            <td class="px-1 py-2.5">
              <div
                class="w-4 h-4 mx-auto rounded-full"
                :class="sample.path_issues ? 'bg-red-600' : 'bg-green-600'"
              >
                <span>&nbsp;</span>
              </div>
            </td>
            <td class="px-1 py-2.5">
              <div v-if="task.organisation_name == 'ISEC'">
                <div
                  class="w-4 h-4 mx-auto rounded-full"
                  :class="{
                    'bg-red-600':
                      (sample.matched &&
                        sample.zone_issues &&
                        sample.lpis_issue) ||
                      (!sample.matched && sample.lpis_issue), // both zone and lpis matching fail
                    'bg-yellow-600':
                      (sample.matched &&
                        (sample.zone_issues || sample.lpis_issue) &&
                        sample.zone_issues != sample.lpis_issue) ||
                      (!sample.matched && !sample.lpis_issue), // if one of the match fail
                    'bg-green-600':
                      sample.matched &&
                      !sample.zone_issues &&
                      !sample.lpis_issue, // if all the match is successful
                  }"
                >
                  <span>&nbsp;</span>
                </div>
              </div>

              <div v-else>
                <div
                  v-if="sample.matched"
                  class="w-4 h-4 mx-auto rounded-full"
                  :class="sample.zone_issues ? 'bg-yellow-600' : 'bg-green-600'"
                >
                  <span>&nbsp;</span>
                </div>
              </div>
            </td>
            <td class="px-1 py-2.5">
              <div class="flex items-center justify-center">
                <span 
                  v-if="getGeoreferenceCount(sample) > 0"
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  :title="`${getGeoreferenceCount(sample)} georeference point${getGeoreferenceCount(sample) !== 1 ? 's' : ''} set for DAFM compliance`"
                >
                  📍 {{ getGeoreferenceCount(sample) }}
                </span>
                <span 
                  v-else
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                  title="No georeference points set"
                >
                  📍 0
                </span>
              </div>
            </td>
            <td class="px-1 py-2.5">
              <div
                v-if="sample.lab_results"
                class="w-4 h-4 mx-auto rounded-full bg-green-600"
              >
                <span>&nbsp;</span>
              </div>
            </td>
            <td class="px-1 py-2.5">
              <inertia-link
                class="hover:text-indigo-600"
                :href="route('app.task.edit-record', sample.hashid)"
              >
                Edit |
              </inertia-link>

              <button
                v-if="$page.props.auth.user.role == 'admin'"
                class="hover:text-indigo-600 text-red-500"
                @click="confirmDelete(sample.hashid)"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-4 flex justify-end">
      <!-- the button should only show for ['admin', 'consultant', 'consultant_user'] and then users that have same organisation as the task -->
      <inertia-link
        v-if="
          ['admin', 'consultant', 'consultant_user'].includes(
            $page.props.auth.user.role
          ) ||
          $page.props.auth.user.organisation_id == task.farmer_organisation_id
        "
        :href="
          route('app.farmer.view', {
            farmer: farmer_id,
            tab: 'farms',
            showPaths: farmteam_task_number,
          })
        "
        class="py-2 px-4 border rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:border-gray-400 disabled:text-gray-400 disabled:bg-gray-200"
      >
        <span>Open Farm Manager</span>
      </inertia-link>

      <button
        v-on:click="goTo(route('app.task.match-unassigned', task.id))"
        class="ml-4 py-2 px-4 border rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:border-gray-400 disabled:text-gray-400 disabled:bg-gray-200"
      >
        <span>Match all Unassigned</span>
      </button>

      <button
        v-on:click="goTo(route('app.task.rematch-all', task.id))"
        class="ml-4 py-2 px-4 border rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:border-gray-400 disabled:text-gray-400 disabled:bg-gray-200"
      >
        <span>Re-match All Samples</span>
      </button>
    </div>
  </div>

  <div v-if="samples.length === 0" class="bg-white px-6 py-4 my-4">
    <strong>No samples found.</strong>
  </div>
</template>

<script>
import Multiselect from "@vueform/multiselect";

export default {
  components: {
    Multiselect,
  },
  props: {
    task: Object,
    tests: Array,
    farmer_id: String,
    samples: Object,
    farmteam_task_number: String,
    availableLabTests: Array,
  },

  data() {
    return {
      showAddSampleForm: false,
      sampleCreationMode: 'single', // 'single' or 'bulk'
      isCreating: false,
      isCreatingBulk: false,
      showGeoreferenceError: false,
      isSelectingGeoreferences: false,
      isBulkGeoreferenceSelection: false,
      bulkStep: 1, // 1: Configure, 2: Lab Tests, 3: Georeferences
      newSample: {
        sample_id: '',
        comment: '',
        lab_test: [],
        georeferences: []
      },
      bulkSamples: {
        count: 5,
        prefix: '',
        suffix: '',
        startNumber: 1,
        labTests: [],
        georeferenceStrategy: 'shared', // 'shared', 'individual', 'skip'
        sharedGeoreferences: []
      }
    };
  },

  mounted() {
    // Listen for map click events for georeference selection
    this.$emitter.on("argicsMapLocation", this.handleGeoreferenceMapClick);
    this.$emitter.on("argicsMapLocation", this.handleBulkGeoreferenceMapClick);
  },

  beforeUnmount() {
    // Clean up event listeners
    this.$emitter.off("argicsMapLocation", this.handleGeoreferenceMapClick);
    this.$emitter.off("argicsMapLocation", this.handleBulkGeoreferenceMapClick);
  },

  computed: {
    transformedLabTests() {
      if (!this.availableLabTests || !Array.isArray(this.availableLabTests)) {
        return {};
      }
      
      const transformed = {};
      this.availableLabTests.forEach(test => {
        if (test.code && test.name) {
          transformed[test.code] = test.name;
        }
      });
      return transformed;
    }
  },



  methods: {
    toggleAddSampleForm(mode = 'single') {
      console.log('toggleAddSampleForm called with mode:', mode);
      console.log('Current state:', {
        showAddSampleForm: this.showAddSampleForm,
        sampleCreationMode: this.sampleCreationMode
      });
      
      if (this.showAddSampleForm && this.sampleCreationMode === mode) {
        // Reset forms when canceling
        console.log('Canceling and resetting forms');
        this.resetForms();
        this.showAddSampleForm = false;
      } else {
        // Switch to the requested mode
        console.log('Switching to mode:', mode);
        this.sampleCreationMode = mode;
        this.showAddSampleForm = true;
        this.resetForms();
        
        if (mode === 'bulk') {
          console.log('Setting bulk step to 1');
          this.bulkStep = 1;
        }
      }
      
      console.log('New state:', {
        showAddSampleForm: this.showAddSampleForm,
        sampleCreationMode: this.sampleCreationMode,
        bulkStep: this.bulkStep
      });
    },

    resetForms() {
      // Reset single sample form
      this.newSample = { sample_id: '', comment: '', lab_test: [], georeferences: [] };
      this.showGeoreferenceError = false;
      this.isSelectingGeoreferences = false;
      
      // Reset bulk sample form
      this.bulkSamples = {
        count: 5,
        prefix: '',
        suffix: '',
        startNumber: 1,
        labTests: [],
        georeferenceStrategy: 'shared',
        sharedGeoreferences: []
      };
      this.isBulkGeoreferenceSelection = false;
      this.bulkStep = 1;
      
      // Clean up map state
      this.showMapInteractionMode = false;
      this.$root.showMapOnMobile = false;
      this.$emitter.emit('stopGeoreferenceSelection', {
        action: 'stop-georeference-selection'
      });
      
      // Clear any georeference markers from the map
      this.$emitter.emit('clearGeoreferences', {
        action: 'clear-georeferences'
      });
    },

    isDuplicateSampleId() {
      if (!this.newSample.sample_id.trim()) return false;
      
      return this.samples.some(sample => 
        sample.id && sample.id.toLowerCase() === this.newSample.sample_id.trim().toLowerCase()
      );
    },

    async createManualSample() {
      // Frontend validation for duplicate Sample ID
      if (this.isDuplicateSampleId()) {
        if (this.$toast) {
          this.$toast.error('A sample with this ID already exists for this task');
        } else {
          alert('A sample with this ID already exists for this task');
        }
        return;
      }

      // Validate georeferences
      if (this.newSample.georeferences.length === 0) {
        this.showGeoreferenceError = true;
        if (this.$toast) {
          this.$toast.error('At least 1 georeference is required for DAFM compliance');
        } else {
          alert('At least 1 georeference is required for DAFM compliance');
        }
        return;
      }

      this.showGeoreferenceError = false;
      this.isCreating = true;
      
      try {
        const response = await axios.post(route('app.task.create-manual-sample', this.task.id), {
          sample_id: this.newSample.sample_id,
          comment: this.newSample.comment || null,
          lab_tests: this.newSample.lab_test,
          manual_georeferences: this.newSample.georeferences
        });

        // Success - reset form and close it
        this.newSample = { sample_id: '', comment: '', lab_test: [], georeferences: [] };
        this.showAddSampleForm = false;
        this.showGeoreferenceError = false;
        this.isSelectingGeoreferences = false;
        
        // Clean up map state
        this.showMapInteractionMode = false;
        this.$root.showMapOnMobile = false;
        this.$emitter.emit('stopGeoreferenceSelection', {
          action: 'stop-georeference-selection'
        });
        
        // Clear georeference markers from the map
        this.$emitter.emit('clearGeoreferences', {
          action: 'clear-georeferences'
        });
        
        // Refresh only the samples data to show the new sample
        this.$inertia.reload({ only: ['samples'] });
        
        // Show success message if toast is available
        if (this.$toast) {
          this.$toast.success('Manual sample created successfully!');
        }
      } catch (error) {
        // Show error message if toast is available
        if (this.$toast) {
          this.$toast.error('Failed to create manual sample. Please try again.');
        }
      } finally {
        this.isCreating = false;
      }
    },

    startGeoreferenceSelection() {
      // Enter multi-point selection mode
      this.isSelectingGeoreferences = true;
      this.showMapInteractionMode = true;
      this.$root.showMapOnMobile = true;
      
      if (this.$toast) {
        this.$toast.info('Multi-point selection active. Click points on the map to add georeferences.');
      }

      // Enable point selection mode on map
      this.$emitter.emit('addFarmPoint', {
        action: 'georeference-selection',
        type: 'georeference-selection',
        multiPoint: true
      });
    },

    finishGeoreferenceSelection() {
      // Exit multi-point selection mode
      this.isSelectingGeoreferences = false;
      this.showMapInteractionMode = false;
      this.$root.showMapOnMobile = false;

      // Disable point selection mode on map
      this.$emitter.emit('stopGeoreferenceSelection', {
        action: 'stop-georeference-selection'
      });

      if (this.$toast) {
        this.$toast.success(`Selection complete! ${this.newSample.georeferences.length} point(s) selected.`);
      }
    },

    clearAllGeoreferences() {
      if (this.$swal) {
        this.$swal.fire({
          title: 'Clear all georeferences?',
          text: 'This will remove all selected points from the map.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, clear all',
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            this.newSample.georeferences = [];
            this.$emitter.emit('clearGeoreferences', {
              action: 'clear-georeferences'
            });
            if (this.$toast) {
              this.$toast.success('All georeferences cleared.');
            }
          }
        });
      } else {
        // Fallback if SweetAlert is not available
        if (confirm('Clear all georeferences? This will remove all selected points.')) {
          this.newSample.georeferences = [];
          this.$emitter.emit('clearGeoreferences', {
            action: 'clear-georeferences'
          });
        }
      }
    },

    removeGeoreference(index) {
      this.newSample.georeferences.splice(index, 1);
      
      // Update the map to reflect the removal
      this.$emitter.emit('updateGeoreferences', {
        action: 'update-georeferences',
        georeferences: this.newSample.georeferences
      });

      if (this.$toast) {
        this.$toast.info('Georeference point removed.');
      }
    },

    handleGeoreferenceMapClick(payload) {
      console.log('Received georeference payload:', payload);
      
      // Only handle clicks when in georeference selection mode
      if (payload.action !== "georeference-selection") {
        console.log('Ignoring payload with action:', payload.action);
        return;
      }

      // Keep map visible and active during multi-point selection mode
      // Only hide map if we're not in multi-point selection mode
      if (!this.isSelectingGeoreferences) {
        this.$root.showMapOnMobile = false;
        this.showMapInteractionMode = false;
      } else {
        // In multi-point mode, keep map visible and active
        console.log('Multi-point mode active - keeping map open for more selections');
      }

      // Extract coordinates from the payload
      if (payload.identifier && payload.identifier.length >= 2) {
        const coords = payload.identifier;
        const lng = coords[0]; // longitude
        const lat = coords[1]; // latitude

        console.log('Extracted coordinates:', { lat, lng });

        // Add the new georeference point
        this.newSample.georeferences.push({
          lat: lat,
          lng: lng
        });

        this.showGeoreferenceError = false;

        // Show success feedback - different messages for single vs multi-point mode
        if (this.$toast) {
          if (this.isSelectingGeoreferences) {
            this.$toast.success(`Added point ${this.newSample.georeferences.length}. Click more points or finish selection.`);
          } else {
            this.$toast.success(`Added georeference point ${this.newSample.georeferences.length}`);
          }
        }

        // Emit event to keep the point visible on map
        this.$emitter.emit('updateGeoreferences', {
          action: 'update-georeferences',
          georeferences: this.newSample.georeferences
        });
      } else {
        console.log('Invalid coordinates in payload:', payload);
      }
    },

    goTo(url) {
      window.location.href = url;
    },

    getGeoreferenceCount(sample) {
      // For manual samples with georeferences, check manual_georeferences
      if (sample.manual_georeferences && Array.isArray(sample.manual_georeferences) && sample.manual_georeferences.length > 0) {
        return sample.manual_georeferences.length;
      }
      
      // For SMAP samples, use the same logic as the map to extract valid marker coordinates
      if (sample.markers && Array.isArray(sample.markers) && sample.markers.length > 0 && sample.paths_lat_lon) {
        const paths = sample.paths_lat_lon[0] || [];
        let validMarkerCount = 0;

        // Count only markers that have valid coordinates
        sample.markers.forEach(markerIndex => {
          // Handle case where markerIndex might be an array with a single value
          const actualIndex = Array.isArray(markerIndex) ? markerIndex[0] : markerIndex;
          
          if (paths[actualIndex] && Array.isArray(paths[actualIndex]) && paths[actualIndex].length >= 2) {
            validMarkerCount++;
          }
        });

        return validMarkerCount;
      }
      
      return 0;
    },

    // Bulk sample creation methods
    generateSampleIdPreviews() {
      const previews = [];
      for (let i = 0; i < this.bulkSamples.count; i++) {
        const number = this.bulkSamples.startNumber + i;
        const sampleId = `${this.bulkSamples.prefix}${number}${this.bulkSamples.suffix}`;
        previews.push(sampleId);
      }
      return previews;
    },

    startBulkGeoreferenceSelection() {
      this.isBulkGeoreferenceSelection = true;
      this.showMapInteractionMode = true;
      this.$root.showMapOnMobile = true;
      
      // Start georeference selection mode on the map
      this.$emitter.emit('startGeoreferenceSelection', {
        action: 'start-georeference-selection',
        mode: 'bulk'
      });
    },

    finishBulkGeoreferenceSelection() {
      this.isBulkGeoreferenceSelection = false;
      this.showMapInteractionMode = false;
      this.$root.showMapOnMobile = false;
      
      // Stop georeference selection mode on the map
      this.$emitter.emit('stopGeoreferenceSelection', {
        action: 'stop-georeference-selection'
      });
    },

    clearAllBulkGeoreferences() {
      this.bulkSamples.sharedGeoreferences = [];
      
      // Clear georeference markers from the map
      this.$emitter.emit('clearGeoreferences', {
        action: 'clear-georeferences'
      });

      if (this.$toast) {
        this.$toast.info('All georeference points cleared.');
      }
    },

    removeBulkGeoreference(index) {
      this.bulkSamples.sharedGeoreferences.splice(index, 1);
      
      // Update the map to reflect the removal
      this.$emitter.emit('updateGeoreferences', {
        action: 'update-georeferences',
        georeferences: this.bulkSamples.sharedGeoreferences
      });

      if (this.$toast) {
        this.$toast.info('Georeference point removed.');
      }
    },

    handleBulkGeoreferenceMapClick(payload) {
      // Only handle clicks when in bulk georeference selection mode
      if (payload.action !== "georeference-selection" || !this.isBulkGeoreferenceSelection) {
        return;
      }

      // Validate coordinates
      if (payload.lat && payload.lng && 
          typeof payload.lat === 'number' && typeof payload.lng === 'number' &&
          payload.lat >= -90 && payload.lat <= 90 && 
          payload.lng >= -180 && payload.lng <= 180) {
        
        // Add the georeference point
        this.bulkSamples.sharedGeoreferences.push({
          lat: payload.lat,
          lng: payload.lng
        });

        if (this.$toast) {
          this.$toast.success(`Georeference point ${this.bulkSamples.sharedGeoreferences.length} added`);
        }

        // Emit event to keep the point visible on map
        this.$emitter.emit('updateGeoreferences', {
          action: 'update-georeferences',
          georeferences: this.bulkSamples.sharedGeoreferences
        });
      } else {
        console.log('Invalid coordinates in bulk payload:', payload);
      }
    },

    async createBulkSamples() {
      this.isCreatingBulk = true;

      try {
        const samplePreviews = this.generateSampleIdPreviews();
        
        // Check for duplicate sample IDs
        const duplicates = samplePreviews.filter(sampleId => 
          this.samples.some(sample => 
            sample.id && sample.id.toLowerCase() === sampleId.toLowerCase()
          )
        );

        if (duplicates.length > 0) {
          if (this.$toast) {
            this.$toast.error(`Duplicate sample IDs found: ${duplicates.join(', ')}`);
          } else {
            alert(`Duplicate sample IDs found: ${duplicates.join(', ')}`);
          }
          return;
        }

        // Prepare bulk samples data
        const bulkData = {
          samples: samplePreviews.map(sampleId => ({
            sample_id: sampleId,
            comment: `Bulk created sample`,
            lab_tests: this.bulkSamples.labTests,
            manual_georeferences: this.bulkSamples.georeferenceStrategy === 'shared' 
              ? this.bulkSamples.sharedGeoreferences 
              : []
          })),
          task_id: this.task.id
        };

        const response = await this.$inertia.post(
          route('app.task.create-bulk-samples', { task: this.task.id }),
          bulkData,
          {
            preserveState: false,
            preserveScroll: true,
            onSuccess: () => {
              // Reset form and close
              this.resetForms();
              this.showAddSampleForm = false;
              
              if (this.$toast) {
                this.$toast.success(`Successfully created ${samplePreviews.length} samples!`);
              }
            },
            onError: (errors) => {
              console.error('Bulk creation errors:', errors);
              if (this.$toast) {
                this.$toast.error('Failed to create bulk samples. Please try again.');
              }
            }
          }
        );

      } catch (error) {
        console.error('Bulk sample creation error:', error);
        if (this.$toast) {
          this.$toast.error('Failed to create bulk samples. Please try again.');
        }
      } finally {
        this.isCreatingBulk = false;
      }
    },

    confirmDelete(sample) {
      const vm = this;
      this.$swal
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        })
        .then((result) => {
          if (result.isConfirmed) {
            vm.$inertia.delete(route("app.task.delete-record", { sample }));
          }
        });
    },
  },
};
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
